/**
 * The supplier's questionnaire — the one page in this product seen by people who
 * are not our customers.
 *
 * WHO IS LOOKING AT THIS
 * Somebody at a supplier who did not ask for it, has no account, may never have
 * heard of us, and has been handed a 147-question security review by a customer
 * they cannot refuse. Everything here follows from that:
 *
 *   * IT EXPLAINS ITSELF FIRST. Who is asking, what for, what happens to the
 *     answers, and how long they have — before the first question. An
 *     unexplained form from an unfamiliar domain asking about your security
 *     controls looks exactly like phishing, and a reasonable person would close
 *     the tab. So the page says plainly who sent it and includes the message
 *     that person wrote.
 *   * NO APP SHELL, NO NAVIGATION, NO SIGN-IN. There is nothing else here for
 *     them, and offering a sign-in box to someone with no account is a dead end
 *     that also makes the page look like a credential-harvesting attempt.
 *   * ANSWERS SAVE AS THEY GO, with the state of that saving always visible.
 *     Nobody finishes 147 questions in one sitting, and losing an hour of work
 *     to a closed tab is unforgivable — more so when the person doing the work
 *     gets nothing out of it.
 *   * ONE SECTION AT A TIME, with progress that never leaves the screen. The
 *     alternative — 147 questions in one scroll — is where third-party
 *     assessments go to die.
 *   * SUBMITTING IS EXPLAINED BEFORE IT IS OFFERED: what it does, that it cannot
 *     be undone, and how many questions are still blank.
 *
 * The token in the URL is the only credential; the page never asks for anything
 * else. Every failure — expired, already submitted, unknown — comes back from the
 * API as one generic code, and this page turns it into one honest explanation
 * that covers all of them, with a route out (talk to the sender) rather than a
 * dead end.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { Button, Pill, ProgressBar, Skeleton, ErrorNotice, Spinner } from '../components/ui';
import { IconShield, IconChevron } from '../components/icons';
import { cn } from '../lib/cn';

interface PublicOption { id: string; label: string; requiresDetail: boolean }

interface PublicQuestion {
  id: string; code: string; text: string; categoryPath: string | null;
  guidance: string | null; evidenceGuidance: string | null;
  type: string; isRequired: boolean;
  options: PublicOption[];
  answer: { optionId: string | null; text: string | null } | null;
}

interface PublicSection {
  code: string; title: string; answered: number; total: number;
  questions: PublicQuestion[];
}

interface PublicAssessment {
  requestedBy: string;
  vendorName: string;
  message: string;
  form: { name: string; description: string | null };
  language: string;
  status: string;
  dueDate: string;
  linkExpiresAt: string;
  answeredCount: number;
  totalQuestions: number;
  sections: PublicSection[];
}

/** What the local draft holds for one question before and after it is saved. */
interface LocalAnswer { optionId: string | null; text: string | null }

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

export function PublicAssessmentPage() {
  const { token = '' } = useParams<{ token: string }>();
  const { t, i18n } = useTranslation();

  const [sectionIndex, setSectionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, LocalAnswer>>({});
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [reviewing, setReviewing] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [submitted, setSubmitted] = useState<{ complianceScore: number } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['public-assessment', token],
    queryFn: () => api.get<PublicAssessment>(`/public/assessment/${token}`),
    enabled: token.length > 0,
    // The token is the only credential and it does not refresh; a retry on a
    // refused link would just repeat the refusal.
    retry: false,
  });

  /**
   * Follow the sender's chosen language, not the browser's.
   *
   * The questionnaire was issued in a language somebody picked deliberately, and
   * the recipient is more likely to share it than to share our default. Applied
   * to the whole document because this page is the whole document.
   */
  useEffect(() => {
    if (data?.language && data.language !== i18n.language) void i18n.changeLanguage(data.language);
  }, [data?.language, i18n]);

  // Seed the local draft from what the server already holds, once.
  const seeded = useRef(false);
  useEffect(() => {
    if (!data || seeded.current) return;
    const initial: Record<string, LocalAnswer> = {};
    for (const section of data.sections) {
      for (const question of section.questions) {
        if (question.answer) {
          initial[question.id] = {
            optionId: question.answer.optionId,
            text: question.answer.text,
          };
        }
      }
    }
    setAnswers(initial);
    seeded.current = true;
  }, [data]);

  const allQuestions = useMemo(
    () => (data?.sections ?? []).flatMap((section) => section.questions),
    [data],
  );
  const questionById = useMemo(
    () => new Map(allQuestions.map((question) => [question.id, question])),
    [allQuestions],
  );

  /**
   * An answer that the server will refuse: an option flagged `requiresDetail`
   * with nothing written in the box.
   *
   * Used in three places, and it matters in all of them — the answer is not
   * sent, not counted towards progress, and not marked complete. Showing it as
   * saved and then quietly failing on the server would be the worst of both.
   */
  const needsExplanation = (question: PublicQuestion, answer: LocalAnswer | undefined) => {
    const chosen = question.options.find((o) => o.id === answer?.optionId);
    return (chosen?.requiresDetail ?? false) && !answer?.text?.trim();
  };

  const save = useMutation({
    mutationFn: (payload: Array<{ questionId: string; optionId?: string | null; text?: string | null }>) =>
      api.put<{ answeredCount: number; totalQuestions: number }>(
        `/public/assessment/${token}`,
        { answers: payload },
      ),
    onMutate: () => { setSaveState('saving'); setSaveError(null); },
    onSuccess: () => setSaveState('saved'),
    onError: () => {
      setSaveState('error');
      // Deliberately not the generic translator: a save failure here needs to
      // say "your last change may not be stored", which is the only thing the
      // person needs to know and act on.
      setSaveError(t('tprm.public.saveFailed'));
    },
  });

  /**
   * Queue a change and flush it after a short pause.
   *
   * Debounced rather than saved on every keystroke: a free-text answer would
   * otherwise be one request per character. The pending map is keyed by question,
   * so answering fast coalesces into one write instead of racing.
   */
  const pending = useRef<Record<string, LocalAnswer>>({});
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = () => {
    const entries = Object.entries(pending.current);
    pending.current = {};

    // Answers still missing their required explanation are held back rather
    // than sent and refused: a rejected request would raise "your change may not
    // have been saved", which is alarming and wrong — nothing is lost, the
    // answer is simply not finished. It goes as soon as they type the reason.
    const batch = entries
      .filter(([questionId, answer]) => {
        const question = questionById.get(questionId);
        return !question || !needsExplanation(question, answer);
      })
      .map(([questionId, answer]) => ({
        questionId,
        optionId: answer.optionId,
        text: answer.text,
      }));

    if (batch.length > 0) save.mutate(batch);
    else if (entries.length > 0) setSaveState('idle');
  };

  const queue = (questionId: string, answer: LocalAnswer) => {
    setAnswers((current) => ({ ...current, [questionId]: answer }));
    pending.current[questionId] = answer;
    setSaveState('saving');
    if (timer.current) clearTimeout(timer.current);
    // 700ms: long enough to absorb typing, short enough that clicking an option
    // and immediately closing the tab still lands.
    timer.current = setTimeout(flush, 700);
  };

  // Anything still queued when the component goes away must be sent. Without
  // this, the last answer before a navigation is silently lost.
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
    flush();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = useMutation({
    mutationFn: () =>
      api.post<{ complianceScore: number; answeredCount: number; totalQuestions: number }>(
        `/public/assessment/${token}/submit`,
        { confirmed: true },
      ),
    onSuccess: (result) => setSubmitted({ complianceScore: result.complianceScore }),
    onError: () => setSaveError(t('tprm.public.submitFailed')),
  });

  /**
   * Answered means an option was chosen, or free text was written — and, where
   * the option demands one, an explanation was given. Anything else has not been
   * stored, so counting it would overstate how far through they are.
   */
  const isAnswered = (question: PublicQuestion) => {
    const answer = answers[question.id];
    if (!answer) return false;
    if (needsExplanation(question, answer)) return false;
    return answer.optionId !== null || (answer.text !== null && answer.text.trim() !== '');
  };

  const answeredTotal = allQuestions.filter(isAnswered).length;
  const progress = allQuestions.length > 0
    ? Math.round((answeredTotal / allQuestions.length) * 100)
    : 0;
  const unanswered = allQuestions.length - answeredTotal;

  const formatDate = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'long' }).format(new Date(iso));

  // --- States before the questionnaire ------------------------------------

  if (isLoading) {
    return (
      <Frame>
        <div className="space-y-3">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-32" />
          <Skeleton className="h-64" />
        </div>
      </Frame>
    );
  }

  /**
   * One explanation for every refusal.
   *
   * The API will not say which of "unknown", "expired", "revoked" or "already
   * submitted" applies — on purpose. So this covers all of them and, more
   * usefully, tells the reader what to do next.
   */
  if (error || !data) {
    return (
      <Frame>
        <div className="oc-card p-6 text-center">
          <div className="mx-auto mb-4 w-12 h-12 rounded-2xl bg-ink/[0.06] grid place-items-center">
            <IconShield className="w-6 h-6 text-ink-muted" />
          </div>
          <h1 className="oc-h2">{t('tprm.public.unavailableTitle')}</h1>
          <p className="oc-muted mt-2 max-w-md mx-auto leading-relaxed">
            {t('tprm.public.unavailableBody')}
          </p>
        </div>
        {/* No footer here on purpose: it points at "the organisation named
            above", and this page deliberately names nobody — it does not even
            confirm that the link ever existed. The body already says to go back
            to whoever sent it. */}
      </Frame>
    );
  }

  if (submitted) {
    return (
      <Frame>
        <div className="oc-card p-6 text-center">
          <div className="mx-auto mb-4 w-12 h-12 rounded-2xl bg-good/10 grid place-items-center">
            <svg viewBox="0 0 20 20" className="w-6 h-6 text-good" fill="currentColor" aria-hidden>
              <path d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0L3.3 9.7a1 1 0 111.4-1.4l3.8 3.8 6.8-6.8a1 1 0 011.4 0z" />
            </svg>
          </div>
          <h1 className="oc-h2">{t('tprm.public.doneTitle')}</h1>
          <p className="oc-muted mt-2 max-w-md mx-auto leading-relaxed">
            {t('tprm.public.doneBody', { organization: data.requestedBy })}
          </p>
          {/* Their own result, shown because they earned it by answering — and
              because a supplier who can see their score can improve it. */}
          <div className="mt-5 inline-flex flex-col items-center rounded-2xl bg-surface-sunken px-6 py-4">
            <span className="text-xs uppercase tracking-wide text-ink-muted">
              {t('tprm.public.yourScore')}
            </span>
            <span className="text-3xl font-semibold tabular-nums text-ink mt-1">
              {submitted.complianceScore}%
            </span>
          </div>
          <p className="mt-4 text-xs text-ink-muted">{t('tprm.public.doneClosed')}</p>
        </div>
        <Footer />
      </Frame>
    );
  }

  const section = data.sections[sectionIndex];
  const dueDays = Math.ceil((new Date(data.dueDate).getTime() - Date.now()) / 86_400_000);

  return (
    <Frame>
      {/* --- Who is asking, and why ---------------------------------------- */}
      <header className="oc-card p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-brand/10 grid place-items-center shrink-0">
            <IconShield className="w-5 h-5 text-brand" />
          </div>
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wide text-ink-muted">
              {t('tprm.public.requestedBy')}
            </p>
            <h1 className="text-xl sm:text-2xl font-semibold text-ink leading-tight text-balance">
              {data.requestedBy}
            </h1>
            <p className="oc-muted mt-1">
              {t('tprm.public.forVendor', { vendor: data.vendorName })}
            </p>
          </div>
        </div>

        <h2 className="mt-5 font-medium text-ink">{data.form.name}</h2>
        {data.form.description && (
          <p className="oc-muted mt-1 leading-relaxed">{data.form.description}</p>
        )}

        {/* The sender's own words, so the page matches the e-mail that brought
            them here — the simplest thing that makes this look legitimate. */}
        {data.message && (
          <blockquote className="mt-4 border-l-2 border-brand/30 pl-3 text-sm text-ink-secondary
                                 whitespace-pre-wrap leading-relaxed">
            {data.message}
          </blockquote>
        )}

        <div className="flex flex-wrap items-center gap-1.5 mt-4">
          <span className="oc-pill-neutral">
            {t('tprm.public.due', { date: formatDate(data.dueDate) })}
          </span>
          {dueDays >= 0 && dueDays <= 7 && (
            <Pill tone="warning">{t('tprm.daysLeft', { count: dueDays })}</Pill>
          )}
          <span className="oc-pill-neutral">
            {t('tprm.questionCount', { count: data.totalQuestions })}
          </span>
        </div>

        <p className="mt-4 text-xs text-ink-muted leading-relaxed">
          {t('tprm.public.assurance', { organization: data.requestedBy })}
        </p>
      </header>

      {/* --- Progress, always in view -------------------------------------- */}
      <div className="sticky top-0 z-20 -mx-4 px-4 py-3 bg-surface-sunken/95 backdrop-blur
                      border-b border-line">
        {/* ProgressBar is already a full-width flex row; wrapping it in another
            flex container collapses its track to nothing. */}
        <ProgressBar value={progress} label={t('tprm.public.progressLabel')} />
        <div className="flex items-center justify-between gap-3 mt-1.5">
          <span className="text-xs text-ink-muted">
            {t('tprm.progress', { answered: answeredTotal, total: allQuestions.length })}
          </span>
          <SaveIndicator state={saveState} />
        </div>
      </div>

      {saveError && <ErrorNotice message={saveError} />}

      {reviewing ? (
        <ReviewPanel
          token={token}
          unanswered={unanswered}
          total={allQuestions.length}
          answered={answeredTotal}
          organization={data.requestedBy}
          confirmed={confirmed}
          onConfirmedChange={setConfirmed}
          submitting={submit.isPending}
          onBack={() => setReviewing(false)}
          onSubmit={() => { flush(); submit.mutate(); }}
        />
      ) : (
        <>
          {/* --- Section navigator ------------------------------------------ */}
          {/* The full review has 49 sections. Left unbounded, the navigator is a
              wall of chips that buries the questions on a phone, so it scrolls
              within a fixed height on small screens and opens up from `sm`. */}
          <nav className="oc-card !p-3" aria-label={t('tprm.public.sections')}>
            <ul className="flex flex-wrap gap-1.5 max-h-28 sm:max-h-none overflow-y-auto">
              {data.sections.map((entry, index) => {
                const done = entry.questions.filter(isAnswered).length;
                const complete = done === entry.total;
                return (
                  <li key={entry.code}>
                    <button
                      onClick={() => setSectionIndex(index)}
                      aria-current={index === sectionIndex}
                      className={cn(
                        'min-h-[34px] px-2.5 rounded-lg text-xs font-medium transition-colors',
                        index === sectionIndex
                          ? 'bg-brand text-white'
                          : complete
                            ? 'bg-good/10 text-good'
                            : 'bg-ink/[0.06] text-ink-secondary',
                      )}
                    >
                      {entry.code} · {done}/{entry.total}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          {section && (
            <section className="space-y-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-ink-muted">
                  {t('tprm.public.sectionOf', {
                    current: sectionIndex + 1,
                    total: data.sections.length,
                  })}
                </p>
                <h2 className="oc-h2 text-balance">{section.title}</h2>
              </div>

              <ul className="space-y-3">
                {section.questions.map((question) => (
                  <li key={question.id}>
                    <QuestionCard
                      question={question}
                      answer={answers[question.id]}
                      onChange={(answer) => queue(question.id, answer)}
                    />
                  </li>
                ))}
              </ul>

              {/* Section paging, thumb-reachable at the bottom where the reader
                  actually finishes. */}
              <div className="flex items-center justify-between gap-2 pt-1">
                <Button
                  disabled={sectionIndex === 0}
                  onClick={() => { setSectionIndex((i) => Math.max(0, i - 1)); window.scrollTo(0, 0); }}
                >
                  <IconChevron className="w-4 h-4 rotate-180" aria-hidden />
                  {t('common.previous')}
                </Button>
                {sectionIndex < data.sections.length - 1 ? (
                  <Button
                    variant="primary"
                    onClick={() => { setSectionIndex((i) => i + 1); window.scrollTo(0, 0); }}
                  >
                    {t('common.next')}
                    <IconChevron className="w-4 h-4" aria-hidden />
                  </Button>
                ) : (
                  <Button variant="primary" onClick={() => { flush(); setReviewing(true); window.scrollTo(0, 0); }}>
                    {t('tprm.public.review')}
                  </Button>
                )}
              </div>
            </section>
          )}

          <div className="text-center">
            <button
              onClick={() => { flush(); setReviewing(true); window.scrollTo(0, 0); }}
              className="text-sm text-brand hover:underline min-h-[44px] px-4"
            >
              {t('tprm.public.reviewAndSubmit')}
            </button>
          </div>
        </>
      )}

      <Footer expiresAt={formatDate(data.linkExpiresAt)} />
    </Frame>
  );
}

// ---------------------------------------------------------------------------

/**
 * The page's own shell.
 *
 * Not components/Shell.tsx: that one carries navigation, an organisation
 * switcher and a sign-out button, none of which mean anything to a supplier. A
 * generous max-width and a single column, because this page has exactly one job.
 */
function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-dvh bg-surface-sunken">
      <div className="mx-auto w-full max-w-3xl px-4 py-6 sm:py-10 space-y-4
                      pb-[max(2rem,env(safe-area-inset-bottom))] animate-fade-in">
        {children}
      </div>
    </div>
  );
}

function Footer({ expiresAt }: { expiresAt?: string }) {
  const { t } = useTranslation();
  return (
    <footer className="pt-2 text-center space-y-1">
      {expiresAt && (
        <p className="text-xs text-ink-muted">
          {t('tprm.public.linkExpires', { date: expiresAt })}
        </p>
      )}
      {/* No marketing, no sign-in, no links out. A supplier who wants to check
          this is genuine should talk to the customer who sent it, and the page
          says so rather than pointing them at us. */}
      <p className="text-xs text-ink-muted">{t('tprm.public.verifyHint')}</p>
    </footer>
  );
}

/** Saving state, spelled out in words rather than left to an icon. */
function SaveIndicator({ state }: { state: SaveState }) {
  const { t } = useTranslation();
  if (state === 'idle') return null;
  if (state === 'saving') {
    return (
      <span className="flex items-center gap-1.5 text-xs text-ink-muted">
        <Spinner className="w-3.5 h-3.5" />
        {t('tprm.public.saving')}
      </span>
    );
  }
  if (state === 'error') {
    return <span className="text-xs text-critical font-medium">{t('tprm.public.saveFailedShort')}</span>;
  }
  return <span className="text-xs text-good font-medium">{t('tprm.public.saved')}</span>;
}

// ---------------------------------------------------------------------------

/**
 * One question.
 *
 * Radio inputs for a choice question and a textarea for a free-text one — the
 * seeded questionnaires are a mix of the two, and roughly a third of them have no
 * options at all. Guidance sits behind a disclosure rather than on the page: it
 * is genuinely useful (it carries the original VSAQ's explanation of what a good
 * answer looks like) and it is far too long to show 147 times.
 */
function QuestionCard({ question, answer, onChange }: {
  question: PublicQuestion;
  answer: LocalAnswer | undefined;
  onChange: (answer: LocalAnswer) => void;
}) {
  const { t } = useTranslation();
  const chosen = question.options.find((o) => o.id === answer?.optionId);
  const needsDetail = chosen?.requiresDetail ?? false;
  const isFreeText = question.options.length === 0;
  const hasText = !!answer?.text?.trim();
  // The green edge means "this is stored". An option awaiting its required
  // explanation is not stored, so it does not get one.
  const answered = !!answer && !(needsDetail && !hasText)
    && (answer.optionId !== null || hasText);

  return (
    <div className={cn(
      'oc-card p-4 border-l-4',
      answered ? 'border-l-good/60' : 'border-l-transparent',
    )}>
      <div className="flex items-start justify-between gap-3">
        <p className="text-xs text-ink-muted font-mono">{question.code}</p>
        {question.categoryPath && (
          <span className="oc-pill-neutral shrink-0 max-w-[55%] truncate">
            {question.categoryPath}
          </span>
        )}
      </div>
      <p className="text-sm sm:text-base text-ink mt-1 leading-relaxed">{question.text}</p>

      {question.guidance && (
        <details className="mt-2">
          <summary className="text-xs text-brand cursor-pointer hover:underline min-h-[28px]">
            {t('tprm.public.guidance')}
          </summary>
          <p className="mt-2 text-xs text-ink-secondary leading-relaxed whitespace-pre-wrap
                        border-l-2 border-brand/30 pl-3">
            {question.guidance}
          </p>
        </details>
      )}

      {isFreeText ? (
        <textarea
          className="oc-input mt-3 min-h-[5rem] py-2 resize-y"
          value={answer?.text ?? ''}
          maxLength={4000}
          placeholder={t('tprm.public.textPlaceholder')}
          aria-label={question.text}
          onChange={(e) => onChange({ optionId: null, text: e.target.value })}
        />
      ) : (
        <fieldset className="mt-3">
          <legend className="sr-only">{question.text}</legend>
          <div className="space-y-1.5">
            {question.options.map((option) => (
              <label
                key={option.id}
                className={cn(
                  'flex items-start gap-3 rounded-xl px-3 py-2.5 cursor-pointer min-h-[44px]',
                  'transition-colors',
                  answer?.optionId === option.id
                    ? 'bg-brand/[0.08] ring-1 ring-brand/30'
                    : 'bg-surface-sunken hover:bg-ink/[0.04]',
                )}
              >
                <input
                  type="radio"
                  name={question.id}
                  className="mt-0.5 w-4 h-4 accent-[rgb(var(--brand))]"
                  checked={answer?.optionId === option.id}
                  onChange={() =>
                    // The existing explanation is kept when switching options:
                    // re-typing it because you changed your mind about the
                    // verdict is pure friction.
                    onChange({ optionId: option.id, text: answer?.text ?? null })
                  }
                />
                <span className="text-sm text-ink">{option.label}</span>
              </label>
            ))}
          </div>
        </fieldset>
      )}

      {/* Options flagged `requiresDetail` need an explanation, and the server
          refuses to store the answer without one — so the reason is stated here
          rather than left to a rejected save. */}
      {needsDetail && (
        <div className="mt-3">
          <label className="oc-label" htmlFor={`detail-${question.id}`}>
            {t('tprm.public.explain')}
          </label>
          <textarea
            id={`detail-${question.id}`}
            className="oc-input min-h-[4.5rem] py-2 resize-y"
            value={answer?.text ?? ''}
            maxLength={4000}
            onChange={(e) => onChange({ optionId: answer?.optionId ?? null, text: e.target.value })}
          />
          {!answer?.text?.trim() && (
            <p className="mt-1 text-xs text-warning">{t('tprm.public.explainRequired')}</p>
          )}
        </div>
      )}

      {question.evidenceGuidance && (
        <p className="mt-2 text-xs text-ink-muted leading-relaxed">
          {question.evidenceGuidance}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * The finalise step.
 *
 * States what submitting does, that it cannot be undone, and — the part that
 * matters most — how many questions are still blank, because blanks count
 * against the supplier's score. Then it asks for an explicit acknowledgement,
 * which the server also requires.
 *
 * THE COUNTS COME FROM THE SERVER
 * Everywhere else on this page the counts are local, because they have to
 * update as fast as the person clicks. Here they are re-read from the API, so
 * that what somebody is asked to commit to is what is actually stored. If a save
 * failed quietly, this is the screen where they need to find out — not
 * afterwards. The local numbers stand in only while that request is in flight.
 */
function ReviewPanel({
  token, unanswered, total, answered, organization, confirmed,
  onConfirmedChange, submitting, onBack, onSubmit,
}: {
  token: string;
  unanswered: number; total: number; answered: number; organization: string;
  confirmed: boolean; onConfirmedChange: (value: boolean) => void;
  submitting: boolean; onBack: () => void; onSubmit: () => void;
}) {
  const { t } = useTranslation();

  const { data: summary } = useQuery({
    queryKey: ['public-assessment', token, 'summary'],
    queryFn: () =>
      api.get<{ answeredCount: number; totalQuestions: number; unansweredRequired: number }>(
        `/public/assessment/${token}/summary`,
      ),
    // Always re-read on entering this step: a cached count is exactly the thing
    // this panel must not show.
    staleTime: 0,
    retry: false,
  });

  const shownAnswered = summary?.answeredCount ?? answered;
  const shownTotal = summary?.totalQuestions ?? total;
  const shownUnanswered = summary
    ? Math.max(0, summary.totalQuestions - summary.answeredCount)
    : unanswered;

  return (
    <div className="oc-card p-5 sm:p-6">
      <h2 className="oc-h2">{t('tprm.public.reviewTitle')}</h2>
      <p className="oc-muted mt-1 leading-relaxed">
        {t('tprm.public.reviewBody', { organization })}
      </p>

      <dl className="mt-4 rounded-xl bg-surface-sunken p-4 space-y-2 text-sm">
        <div className="flex justify-between gap-3">
          <dt className="text-ink-secondary">{t('tprm.public.answered')}</dt>
          <dd className="tabular-nums font-medium text-ink">{shownAnswered} / {shownTotal}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="text-ink-secondary">{t('tprm.public.unanswered')}</dt>
          <dd className={cn(
            'tabular-nums font-medium',
            shownUnanswered > 0 ? 'text-warning' : 'text-good',
          )}>
            {shownUnanswered}
          </dd>
        </div>
      </dl>

      {shownUnanswered > 0 && (
        <p className="mt-3 text-sm text-ink-secondary leading-relaxed">
          {t('tprm.public.unansweredWarning', { count: shownUnanswered })}
        </p>
      )}

      <div className="mt-4 rounded-xl border border-warning/30 bg-warning/[0.06] px-4 py-3">
        <p className="text-sm text-ink leading-relaxed">{t('tprm.public.irreversible')}</p>
      </div>

      <label className="flex items-start gap-3 cursor-pointer mt-4">
        <input
          type="checkbox"
          className="mt-0.5 w-4 h-4 accent-[rgb(var(--brand))]"
          checked={confirmed}
          onChange={(e) => onConfirmedChange(e.target.checked)}
        />
        <span className="text-sm text-ink">{t('tprm.public.confirm')}</span>
      </label>

      <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-5">
        <Button onClick={onBack}>{t('tprm.public.backToQuestions')}</Button>
        <Button variant="primary" disabled={!confirmed || submitting} onClick={onSubmit}>
          {submitting ? t('common.saving') : t('tprm.public.submit')}
        </Button>
      </div>
    </div>
  );
}
